import React, { useEffect, useState, useRef, useCallback, useMemo } from "react"

import { getMissions } from "../services/api/v2/mission"
import { getActionItems } from "../services/api/v2/actionItem"

import cx from "classnames"
import { formatDistanceToNow } from "date-fns"
import format from "date-fns/format"
import Skeleton from "./Skeleton"

import { getAiCode, actionItemPermissions } from "../utils/actionItem"
import apiComments from "../services/comment/comment.api"
import { toast } from "react-toastify"
import Messages from "./Messages"
import LazyWrapper from "./LazyWrapper"
import groupBy from "lodash/groupBy"
import Badge from "./Badge"

import { motion, AnimatePresence } from "framer-motion"
import { PiEnvelopeOpenDuotone } from "react-icons/pi"
import Highlighter from "react-highlight-words"

import Panel from "./Panel"
import Button from "./Button"

import RoundAvatar from "./RoundAvatar"
import Tip from "./Tip"
import { getObjectId, getPersonName, getRefId, useDebouncedEffect } from "../utils/utils"

import { addParam, addParams } from "../comp/History"
import { getOrgSignOffs } from "../services/api/v2/signOff"
import SimpleBarReact from "simplebar-react"
import "simplebar/dist/simplebar.min.css"
import "./PanelNotification.scss"
import notificationStore, { useNotifications } from "../services/notification/notificationStore"
import {
    CATEGORY_BY_KEY,
    NOTIFICATION_CATEGORIES,
    buildRows,
    isCategoryVisible,
} from "./notifications/notificationCategories"

import chatStore, { useChat } from "../services/chat/chatStore"
import { chatOrgs } from "./chat/chatUtils"

export { checkForClientPrograms } from "./notifications/notificationCategories"

/** Chat's unread messages, every organisation (the Chats tab's badge once chat is on). */
const selectChatUnread = (s) => Object.values(s.unread).reduce((sum, row) => sum + (row.unread || 0), 0)

const VIEW_STORAGE_KEY = "x-42H2"

const markRead = (id) => notificationStore.markRead(id)

/** The sign-offs waiting on me (ready, I am the approver) across my organisations. */
const loadWaitingSignOffs = async (orgs) => {
    const responses = await Promise.all(
        orgs.map((org) =>
            getOrgSignOffs(getObjectId(org._id), { approver: "me", status: ["ready"], limit: 30 })
                .then((res) => (res?.value || []).map((signOff) => ({ ...signOff, org: org })))
                .catch(() => []),
        ),
    )

    return responses.flat()
}

/** Whoever asked for the sign-off, with their avatar: me, else the org's member, else just the name. */
const requesterOf = (signOff, app) => {
    const id = getRefId(signOff.requestedBy?.ref)

    if (id && id === app.state.person?._id) return app.state.person

    const member = [...(signOff.org?.people || []), ...(signOff.org?.archivedPeople || [])].find(
        (p) => getRefId(p) === id,
    )

    if (member) return member.ref && typeof member.ref === "object" ? member.ref : member

    return { _id: id, firstName: signOff.requestedBy?.name || "" }
}

/** Waiting sign-offs as panel rows: unread until decided, opened on their own view. */
const waitingSignOffRows = (signOffs, { app, close }) =>
    signOffs.map((signOff) => ({
        people: [requesterOf(signOff, app)],
        _id: `waiting-${signOff._id}`,
        _rowKey: `waiting-${signOff._id}`,
        isSignOff: true,
        isWaitingSignOff: true,
        isRead: false,
        createdAt: signOff.updatedAt || signOff.createdAt,
        orgData: signOff.org,
        title: "Ready for your approval",
        body: signOff.title,
        onClick: () => {
            close()
            addParams([
                { param: "sign-off-org", value: getObjectId(signOff.org) },
                { param: "sign-off", value: signOff._id },
            ])
        },
    }))

/**
 * The notification panel. Reads the notification store (services/notification/
 * notificationStore.js) and lays it out by the tabs of
 * comp/notifications/notificationCategories.js; a tab loads and pages its own
 * category from the server when it is opened.
 */
const PanelNotification = ({ app, open, onClose }) => {
    const ranOnce = useRef()
    const list = useNotifications((s) => s.list)
    const categoryState = useNotifications((s) => s.categories)
    const loaded = useNotifications((s) => s.loaded)

    const [view, setView] = useState(localStorage.getItem(VIEW_STORAGE_KEY + app.state.person._id) || "pinned")
    const [showThread, setShowThread] = useState()

    const closeFn = (force) => {
        if (showThread && !force) {
            setShowThread(null)
        } else {
            onClose()
        }
    }

    // The rows' click handlers read the latest closers, not the ones of the
    // render that built them.
    const closersRef = useRef({})
    closersRef.current = { close: closeFn, closePanel: onClose }

    // What waits on me comes from the sign-offs themselves: a sign-off can be ready
    // without a notification (migrated ones, older than the recent page).
    const [waitingSignOffs, setWaitingSignOffs] = useState([])

    useEffect(() => {
        if (!open) return

        let live = true

        loadWaitingSignOffs(app.state.orgs || []).then((list) => {
            if (live) setWaitingSignOffs(list)
        })

        return () => {
            live = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, app.state.orgs])

    const rows = useMemo(() => {
        const ctx = {
            app,
            close: (force) => closersRef.current.close(force),
            closePanel: () => closersRef.current.closePanel(),
            markRead,
        }
        const built = buildRows(list, ctx)
        const waitingIds = new Set(waitingSignOffs.map((s) => s._id))

        built.signOffs = [
            ...waitingSignOffRows(waitingSignOffs, ctx),
            ...built.signOffs.filter((n) => !(n.type === "signoff_ready" && waitingIds.has(String(n.typeId)))),
        ]

        return built
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [list, waitingSignOffs, app.state.orgs, app.state.missingTimesheets, app.state.person?._id])

    // With chat on, the Chats tab opens the chat itself. Off for someone whose only
    // organisations are GT's; a guest on missions with no organisation of their own has it.
    const chatAvailable = useMemo(
        () => !app.state.orgs?.length || chatOrgs(app.state.orgs).length > 0,
        [app.state.orgs],
    )
    const chatUnread = useChat(selectChatUnread)

    const visible = NOTIFICATION_CATEGORIES.filter((category) => isCategoryVisible(category, app, rows[category.key]))
    const current =
        visible.find((category) => category.key === view) && !(chatAvailable && view === "chats") ? view : "pinned"

    // First visit: with nothing pinned but task chats waiting, open on the chats.
    useEffect(() => {
        if (!loaded || ranOnce.current) return

        ranOnce.current = true

        if (!rows.pinned.length && rows.chats.length && !chatAvailable) setView("chats")
    }, [loaded, rows, chatAvailable])

    useEffect(() => {
        if (open) {
            app.hideKnobs()
        } else {
            app.showKnobs()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    useEffect(() => {
        localStorage.setItem(VIEW_STORAGE_KEY + app.state.person._id, current)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [current])

    // An open tab loads its own category, so it is not limited to the recent page.
    useEffect(() => {
        if (open && loaded && CATEGORY_BY_KEY[current]?.query) {
            notificationStore.loadCategory(current, CATEGORY_BY_KEY[current].query)
        }
    }, [open, loaded, current])

    const selectView = (key) => {
        if (key === "chats" && chatAvailable) {
            onClose()
            chatStore.openChat()
            return
        }

        setShowThread(null)
        setView(key)
    }

    return (
        <Panel
            open={open}
            maxWidth={showThread ? 1050 : 620}
            onClose={() => closeFn()}
            className={"panel-notification"}
            outsideClickIgnoreClass="x-aix-img-msg-view"
            onBlur={() => {
                onClose()
            }}
        >
            <CommsBar
                view={current}
                setView={selectView}
                categories={visible}
                rows={rows}
                counts={chatAvailable ? { chats: chatUnread } : undefined}
            />
            <MessagesBar
                view={current}
                category={CATEGORY_BY_KEY[current]}
                messages={rows[current] || []}
                paging={categoryState[current]}
                setShowThread={setShowThread}
                showThread={showThread}
                app={app}
            />
            {showThread?._id && (
                <Comms
                    key={showThread?._id}
                    app={app}
                    message={showThread}
                    closeFn={() => {
                        setShowThread(null)
                    }}
                />
            )}
        </Panel>
    )
}

const Comms = ({ message, app, closeFn }) => {
    const socketRef = useRef()
    const msgAudio = useRef()
    const threadsRef = useRef()
    const [thread, setThread] = useState([])
    const [ai, setAi] = useState()
    const [mission, setMission] = useState()
    const [orgData, setOrgData] = useState()
    const [topic, setTopic] = useState()
    const [permissions, setPermissions] = useState()
    const [subTask, setSubTask] = useState()

    const onSocketChange = useCallback((changes) => {
        let newComments = threadsRef.current.slice()

        changes.forEach((change, i) => {
            if (change.changeType === "create") {
                newComments.push(change.doc)
                msgAudio?.current.play()
            }
            if (change.changeType === "delete") {
                let myIndex = newComments.findIndex((c) => c._id === change._id)

                newComments.splice(myIndex, 1)
            }
        })
        threadsRef.current = newComments
        setThread(newComments)
    }, [])

    useEffect(() => {
        const dx = async () => {
            const ga = await getActionItems({
                id: message.data.room,
            })

            setAi(ga.value?.[0])
        }

        if (message?.data?.roomName === "ActionItem") dx()
    }, [message?.data?.room])

    useEffect(() => {
        const loadMission = async () => {
            const ma = message.chatProps?.mission?._id
                ? await getMissions({
                      id: message.chatProps?.mission?._id,
                      expand: ["people"],
                      select: [
                          "title",
                          "projectCode",
                          "theme",
                          "org",
                          "people.ref",
                          "people.permission",
                          "people.isProjectManager",
                          "people.invitePending",
                      ],
                  })
                : null

            setMission(ma?.value[0])
        }

        if (message?.chatProps?.mission?._id) loadMission()
    }, [message?.chatProps?.mission?._id])

    useEffect(() => {
        if (!message || !ai || !mission) return
        const theAI = ai //actionItemMap[message?.data?.room]

        const theOrg = app.state.orgs.find((o) => o._id === getObjectId(message.mission?.org))
        const theTopic = message.data.topic

        if (!theAI) {
            toast.warning("🌵 Ooops. This thread is no longer available")
            closeFn()
            return
        }

        const thePermissions = actionItemPermissions({
            actionItem: theAI,
            mission,
            app,
            ignoreDone: true,
        })

        const cM = theAI.checklist?.find((c) => c._id === theTopic)

        setTopic(theTopic)
        setSubTask(cM)

        setOrgData(theOrg)
        setPermissions(thePermissions)

        apiComments
            .actionItem(theAI?._id)
            .getCommentList()
            .then((res) => {
                //thread.filter((c) => (message.topic ? c.topic === topic : !c.topic)) : []
                setThread(res)
                threadsRef.current = res
            })
            .catch((err) => {
                toast.error(err.message)
            })

        socketRef.current = apiComments.actionItem(theAI?._id).onChanges(onSocketChange)

        return () => {
            socketRef?.current?.disconnect()
        }
    }, [message, ai, mission])

    if (!ai || !mission) {
        return (
            <div style={{ display: "flex", flexDirection: "column", width: 300, paddingLeft: 30, paddingTop: 10 }}>
                <div style={{ marginBottom: 9, marginTop: 15, marginLeft: 0 }}>
                    <Skeleton height={50} width={250} />
                </div>
                <div style={{ marginBottom: 9, marginTop: 15, marginLeft: 0 }}>
                    <Skeleton height={70} width={250} />
                </div>
                <div style={{ marginBottom: 9, marginTop: 15, marginLeft: 0 }}>
                    <Skeleton height={40} width={180} />
                </div>
                <div style={{ marginBottom: 9, marginTop: 15, marginLeft: 0 }}>
                    <Skeleton height={90} width={300} />
                </div>
                <div style={{ marginBottom: 9, marginTop: 15, marginLeft: 0 }}>
                    <Skeleton height={70} width={250} />
                </div>
            </div>
        )
    }

    return (
        <div className="x-comms-chat">
            <div className="x-comms-chat-context">
                <div
                    className="dna-round-avatar"
                    style={{ marginRight: 8, backgroundImage: mission?.theme?.backgroundImage }}
                />
                <div>
                    <div>
                        <div
                            className="x-link x-clamp-1"
                            onClick={() => {
                                app.showActionItemDetails(message.data.room, {
                                    showComments: true,
                                    topic: message.data?.topic,
                                })
                                closeFn(true)
                            }}
                        >
                            {getAiCode(ai)} - {ai.title || "No title for this task"}
                        </div>
                    </div>
                    <div
                        className="x-link x-clamp-1 dna-smaller-text dna-light-text"
                        onClick={() => {
                            addParam("mission", ai.missionId)
                            closeFn(true)
                        }}
                    >
                        {mission?.projectCode} {mission?.title}
                    </div>
                </div>
            </div>
            {mission && permissions && (
                <Messages
                    key={message?.data?.room}
                    ai={ai}
                    app={app}
                    permissions={permissions}
                    showComments={true}
                    comments={thread}
                    orgData={orgData}
                    mission={mission}
                    topic={topic}
                />
            )}
            <audio ref={msgAudio} style={{ display: "none" }}>
                <source src="/video/message.mp3" type="audio/mpeg" />
            </audio>
        </div>
    )
}

const unreadOf = (messages = []) => messages.filter((m) => m.isRead !== true).length

/** The tab icons down the side, one per visible category. */
const CommsBar = ({ view, setView, categories, rows, counts }) => (
    <div className="x-comms-bar">
        <ul>
            {categories.map((category) => {
                const { key, Icon } = category

                return (
                    <Badge
                        key={key}
                        badgeContent={counts?.[key] ?? unreadOf(rows[key])}
                        color={category.badgeColor || "primary"}
                    >
                        <li onClick={() => setView(key)} className={cx({ active: view === key })}>
                            <Tip position="right" distance={20} title={category.tip}>
                                <Icon />
                            </Tip>
                        </li>
                    </Badge>
                )
            })}
        </ul>
    </div>
)

/** One tab: its title, "mark all as read", the rows and "load more". */
const MessagesBar = ({ view, category, messages = [], paging, setShowThread, showThread, app }) => {
    const [search] = useState()
    const [showUnread, setShowUnread] = useState()
    const unread = unreadOf(messages)

    const markAllRead = () => {
        notificationStore.markRead(messages.filter((m) => m.isRead !== true && !m.isWaitingSignOff).map((m) => m._id))
    }

    return (
        <div className={cx("x-comms-messages", view)}>
            <h3>{category?.title || "Notifications"}</h3>
            {unread > 0 && !category?.noMarkAll && (
                <div className="x-mark-all-as-read x-link" onClick={markAllRead}>
                    Mark all as read
                </div>
            )}
            <div className="x-comms-filter-bar">
                {view === "chats" && unread > 0 && (
                    <Tip
                        position="left"
                        title="Show unread messages"
                        className={cx("dna-under-con", { active: showUnread })}
                        onClick={() => {
                            setShowUnread(!showUnread)
                        }}
                    >
                        <PiEnvelopeOpenDuotone />
                    </Tip>
                )}
            </div>

            <SimpleBarReact className="x-comms-scroll">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={view}
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -30, opacity: 0, height: 0, overflow: "hidden" }}
                        transition={{ duration: 0.4, delay: 0.3 }}
                    >
                        <Chats
                            app={app}
                            messages={messages}
                            search={search}
                            view={view}
                            empty={category?.empty}
                            loading={paging?.loading && !messages.length}
                            showUnread={showUnread}
                            setShowThread={setShowThread}
                            showThread={showThread}
                        />
                        {paging?.hasMore && (
                            <div className="x-comms-load-more">
                                <Button
                                    className={cx("transparent", { disabled: paging.loading })}
                                    onClick={() => notificationStore.loadMore(view)}
                                >
                                    {paging.loading ? "Loading..." : "Load older"}
                                </Button>
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </SimpleBarReact>
        </div>
    )
}

const Chats = ({ messages, setShowThread, showThread, view, search, app, showUnread, empty, loading }) => {
    let myMessages = useMemo(() => {
        let ms = messages
            .map((message) => {
                return normalizedData({ message, app })
            })
            .filter(Boolean)

        const groupedTaskMessages = groupBy(
            ms.filter((n) => n?.data?.roomName === "ActionItem"),
            "data.room",
        )
        let taskChats = []

        Object.keys(groupedTaskMessages).forEach((room) => {
            const myList = groupedTaskMessages[room].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

            taskChats.push(myList[0])
        })

        ms = ms.filter((c) => c?.data?.roomName !== "ActionItem")

        return [...ms, ...taskChats]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages])

    return (
        <div className="x-comms-chats">
            {loading && (
                <div style={{ marginTop: 20 }}>
                    <Skeleton height={72} width={360} />
                </div>
            )}
            {!loading && messages?.length === 0 && (
                <>
                    <p style={{ marginTop: 30 }}>
                        <b>Nothing to show yet... 👻</b>
                    </p>
                    {empty && <p>{empty}</p>}
                </>
            )}
            {myMessages.map((message) => {
                if (showUnread && message.isRead) {
                    return null
                }

                return (
                    <LazyWrapper key={message?._rowKey || message?._id}>
                        {message?.data?.roomName === "ActionItem" ? (
                            <MessageAi
                                {...{
                                    message,
                                    setShowThread,
                                    showThread,
                                    app,
                                    search,
                                    obj: message.obj,
                                }}
                            />
                        ) : (
                            <Message
                                {...{
                                    message,
                                    setShowThread,
                                    showThread,
                                    app,
                                    search,
                                    obj: message.obj,
                                }}
                            />
                        )}
                    </LazyWrapper>
                )
            })}
        </div>
    )
}

const normalizedData = ({ message, app }) => {
    const isAi = message?.data?.roomName === "ActionItem"
    const isPinned = message.isPinned

    let title = ""
    let person
    let ai
    let subject = ""
    let body = ""
    let system
    let subSubject = ""
    let subTask

    if (isAi) {
        person = message.people?.[0]
        title = getPersonName(person)

        ai = {}

        body = message?.body || "No message..."
    } else if (message.isLeave) {
        person = message.people?.[0]

        if (message.type === "leave_request_rejected") {
            person = {
                avatar: "/img/mAX.png",
                email: "help@missionx.ai",
                ref: { firstName: "mAX", lastName: " " },
            }
            title = "Oh-oh, a leave request was rejected"

            body = "Please have a look at your leaves to see why."
        } else if (message.type === "leave_request_approved") {
            person = {
                avatar: "/img/mAX.png",
                email: "help@missionx.ai",
                ref: { firstName: "mAX", lastName: " " },
            }
            title = "Your leave request was approved"

            body = "Have a look at your leaves for the details."
        } else {
            title =
                getPersonName(person) +
                " " +
                (message.title?.includes("cancelled") ? "cancelled a leave request" : "submitted a leave request")

            body =
                message.title === "Leave requests cancelled"
                    ? "A leave request that may affect your projects was cancelled"
                    : "Your input is is welcomed."
        }

        //
    } else if (message.isEquipment) {
        title = message.title
        body = message.body
        system = true
        person = {
            avatar: message.orgData?.logo || "/img/org-no-logo.png",
        }
    } else if (message.isSignOff || message.isWork) {
        person = message.people?.[0] || {
            avatar: message.orgData?.logo || "/img/org-no-logo.png",
        }
        title = message.title
        body = message.body
    } else if (message.isVendorInvoice) {
        person = message.people?.[0] || {
            avatar: message.orgData?.logo || "/img/org-no-logo.png",
        }
        title = "An invoice is awaiting your approval"
        body = message.title
    } else if (message.system) {
        title = message.title
        subject = message.subject
        body = message.body
        system = true
        person = {
            avatar: message.orgData.logo || "/img/org-no-logo.png",
        }
    } else if (message.type === "payment") {
        title = message.title
        system = true
        subject = message.subject
        body = message.body
        person = {
            avatar: message.orgData.logo || "/img/org-no-logo.png",
        }
    } else if (message.isAllocation) {
        title = message.title

        subject = message.subject
        body = message.body
        person = {
            avatar: message.orgData.logo || "/img/org-no-logo.png",
        }
    } else if (message.isInvite) {
        person = message.people?.[0]

        if (!person || person.firstName === "" || person.lastName === "") {
            return null
        }
        title = getPersonName(person)

        subject = message.mission?.title || message.org?.title
        body =
            person?.firstName +
            " has invited you to join the " +
            (message.type === "orgInvite" ? "Organization 🎉" : "Project 🎉🎉🎉") +
            " Welcome aboard!"
    }

    return {
        ...message,
        system: system,
        subTask,
        obj: {
            isAi,
            isPinned,
            title,
            subSubject,
            ai,
            subject,
            person,
            body,
        },
    }
}

const MessageAi = ({ message, setShowThread, showThread, app, search, obj }) => {
    const [ai, setAi] = useState()
    const [mission, setMission] = useState()
    const [person, setPerson] = useState()
    const [loaded, setLoaded] = useState()

    const ld = async () => {
        const ga = await getActionItems({
            id: message.data.room,
            select: ["title", "code", "missionId"],
        })

        const ma = ga.value[0]
            ? await getMissions({
                  id: ga.value[0].missionId,
                  select: ["_id", "title", "projectCode", "theme", "org"],
              })
            : null

        setPerson(message.people?.[0])
        setMission(ma?.value[0])
        setAi(ga?.value[0])
        setLoaded(true)
    }

    useDebouncedEffect(
        () => {
            ld()
        },
        [],
        100,
    )

    if (loaded && (!ai || !mission)) {
        return null
    }

    if (!ai) {
        return (
            <div style={{ marginBottom: 9, marginTop: 15, marginLeft: 0 }}>
                <Skeleton height={72} width={Math.floor(Math.random() * (400 - 250 + 1)) + 250} />
            </div>
        )
    }

    return (
        <div
            className={cx("x-comms-message", {
                read: message.isRead,
            })}
            onMouseEnter={message.onMouseEnter}
            onClick={() => {
                setShowThread({ ...message, chatProps: { ai, mission } })
                markRead(message._id)
            }}
        >
            <RoundAvatar
                person={person}
                width={44}
                noTip={message.system}
                style={{ marginTop: 5, borderRadius: "50%", marginRight: 10, marginLeft: 6 }}
                noFlip={false}
            />
            <div className="x-comms-content ai">
                <div
                    style={{ opacity: 1 }}
                    className={cx("x-comms-message-body", {
                        "x-clamp-2": true,
                    })}
                >
                    <Highlighter
                        highlightClassName="dna-highlight-text"
                        searchWords={[search]}
                        autoEscape={true}
                        textToHighlight={obj?.body || ""}
                    />
                </div>
                {message.createdAt && (
                    <div className="x-comms-distance">
                        <Tip noFlip title={format(new Date(message.createdAt), "eee. MMM. do, yyyy h:mm a")}>
                            <span>{formatDistanceToNow(new Date(message.createdAt), { addSuffix: true })}</span>
                        </Tip>
                    </div>
                )}
                <div className="x-comms-message-header">
                    <div className="x-clamp-2">
                        <Highlighter
                            highlightClassName="dna-highlight-text"
                            searchWords={[search]}
                            autoEscape={true}
                            textToHighlight={`${getAiCode(ai)} ${ai?.title || "Task message"}`}
                        />
                    </div>
                    {mission && (
                        <div className="x-clamp-1">
                            {mission.projectCode} {mission.title}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const Message = ({ message, setShowThread, showThread, app, search, obj }) => {
    return (
        <div
            className={cx("x-comms-message", {
                read: message.isRead,
            })}
            onMouseEnter={message.onMouseEnter}
            onClick={
                obj?.isAi
                    ? () => {
                          setShowThread(message)
                          markRead(message._id)
                      }
                    : message.isAllocation
                      ? () => markRead(message._id)
                      : message.onClick
            }
        >
            <RoundAvatar
                person={message.people?.[0]}
                width={44}
                noTip={message.system}
                style={{ marginTop: 2, borderRadius: "50%", marginRight: 12, marginLeft: 4 }}
                noFlip={false}
            />
            <div className="x-comms-content">
                <div className="x-comms-message-header">
                    <Highlighter
                        highlightClassName="dna-highlight-text"
                        searchWords={[search]}
                        autoEscape={true}
                        textToHighlight={obj?.title || ""}
                    />

                    {message.createdAt && (
                        <Tip noFlip title={format(new Date(message.createdAt), "eee. MMM. do, yyyy h:mm a")}>
                            <span className="x-comms-distance">
                                {formatDistanceToNow(new Date(message.createdAt), { addSuffix: true })}
                            </span>
                        </Tip>
                    )}
                </div>
                <div className="x-comms-message-subject x-clamp-1">
                    <Highlighter
                        highlightClassName="dna-highlight-text"
                        searchWords={[search]}
                        autoEscape={true}
                        textToHighlight={obj?.subject || ""}
                    />
                </div>
                {obj.subSubject && <div className="x-comms-message-sub-subject">{obj.subSubject}</div>}
                <div
                    className={cx("x-comms-message-body", {
                        "x-clamp-2": !message.isAllocation,
                    })}
                >
                    <Highlighter
                        highlightClassName="dna-highlight-text"
                        searchWords={[search]}
                        autoEscape={true}
                        textToHighlight={obj?.body || ""}
                    />
                </div>
            </div>
        </div>
    )
}

PanelNotification.displayName = "PanelNotification"
export default PanelNotification
